import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  ViewSewerObjects,
  ViewSewerObjectsFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { ProjectMenuItemsModel } from "../ProjectMenuItems";
import PageLayout from "../../_layout/PageLayout";
import SewerObjectsMap from "../../sewer-objects/SewerObjectsMap";
import { api } from "../../../services/api/ApiService";
import { SewerObjectsMapStorageModel } from "../../../services/sewer-object/SewerObjectsMapStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import { CurrentProjectStorageModel } from "../../../services/project/CurrentProjectStorage";
import DataGrid from "../../../components/datatable/DataGrid";
import { useHistory } from "react-router";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { translationService } from "../../../services/translation/TranslationService";
import Svg from "../../../components/svg/Svg";
import { Info } from "../../../components/svg/svgImageList";
import SewerObjectsMapLegend from "../../../components/map/SewerObjectsMapLegend";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const projectId = useMemo(() => urlParamsService.getProjectId()!, [urlParamsService]);

  const [currentProjectStorage] = useState(() =>
    CurrentProjectStorageModel.create({ projectId: projectId }, { api })
  );

  const [mapStorage] = useState(() =>
    SewerObjectsMapStorageModel.create({ inputProjectId: projectId, autoLoad: false }, { api })
  );

  const projectMenuItems = useMemo(
    () =>
      ProjectMenuItemsModel.create({
        projectId: projectId,
        isLoading: currentProjectStorage.isLoading,
        numberOfSewerObjects: currentProjectStorage.currentProject.sewerObjectsCount,
      }),
    [
      projectId,
      currentProjectStorage.isLoading,
      currentProjectStorage.currentProject.sewerObjectsCount,
    ]
  );

  const filterMapData = useCallback(async () => {
    if (mapStorage.sewerObjectsStorage.hasFilterApplied) {
      await mapStorage.setFilterData(mapStorage.sewerObjectsStorage.filterData.gridFilter);
    } else {
      await mapStorage.setFilterData(undefined);
    }
    mapStorage.setIsFilterMapDisabled(true);
  }, [mapStorage]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([
      {
        entity: EntityTypeFromPath.Project,
        name: currentProjectStorage.currentProject.name,
      },
    ]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentProjectStorage.currentProject.name, history.location.pathname]);

  useEffect(() => {
    mapStorage.setDefaultSelectedItem(
      mapStorage.sewerObjectsStorage.selectedItemsCount === 1
        ? mapStorage.sewerObjectsStorage.selectedItems[0]
        : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStorage.sewerObjectsStorage.selectedItems[0], mapStorage]);

  return (
    <PageLayout
      id="project-sewer-map-page"
      accessWithOneOfThePermissions={[ViewSewerObjects, ViewSewerObjectsFromAssignedProject]}
      titleKey="ProjectSewerObjectsMapPage.title"
      titleContext={{
        projectName:
          currentProjectStorage.currentProject && currentProjectStorage.currentProject.name,
      }}
      isLoading={currentProjectStorage.isLoading}
      menuItems={{ staticItems: projectMenuItems.getStaticItems() }}
    >
      {currentProjectStorage.currentProject.sewerObjectsCount === 0 ? (
        <div className="message-info">
          <Svg className="svg-info" svg={Info} />
          <span>
            {translationService.translate("project-sewer-objects.no-map-data-part1")}
            <Link to={`sewer-objects`}>
              {translationService.translate("project-sewer-objects.no-map-data-part2")}
            </Link>
            {translationService.translate("project-sewer-objects.no-map-data-part3")}
          </span>
        </div>
      ) : (
        <>
          <div className="map-page-container">
            <div className="map-container">
              <SewerObjectsMap sewerObjectsMapStorage={mapStorage} />
            </div>
            <div className="table-container">
              <DataGrid
                itemTypeKey="general.sewer-objects"
                dataGridStorage={mapStorage.sewerObjectsStorage}
                toolbarCustomButton={{
                  icon: CheckCircleIcon,
                  textKey: "general.data-grid.toolbarFilterMap",
                  callback: filterMapData,
                  isDisabled: mapStorage.isFilterMapDisabled,
                }}
                filterModelChangeCallback={() => mapStorage.setIsFilterMapDisabled(false)}
                hasDefaultColumnVisibility
                singleSelection
                isOnSide
              />
              <SewerObjectsMapLegend />
            </div>
          </div>
        </>
      )}
    </PageLayout>
  );
});
